/**
 * Centralized section-gating map (session 73).
 *
 * Single source of truth linking each situational gate (a boolean column on
 * `clients`) to the section slugs it controls. Consumed by BOTH:
 *   - the Guided wizard (`GuidedStagePage.tsx`) — hides gated sections whose
 *     gate is explicitly false; and
 *   - the category page (`GenericCategoryPage.tsx`) — renders an auto-NA
 *     ("Not applicable — Mark as applicable") affordance over gated sections.
 *
 * Gate keys are the camelCase `clients` columns (also the keys accepted by
 * `PATCH /api/clients/:id/situation-flags`).
 *
 * Lives under shared/constants/ (excluded from the slug check) and references
 * SECTION_SLUGS / CATEGORY_SLUGS constants — never raw slug strings.
 */

import { SECTION_SLUGS } from './sections';
import { CATEGORY_SLUGS } from './categories';

/**
 * Every gate the wizard filters on (4 in-scope auto-NA gates + 2 wizard-only),
 * plus `hasDependents` (s79): a co-gate for the Children & Dependents category
 * only. It controls no sections of its own — it widens the category-level N/A to
 * "children OR dependents" via CATEGORY_OR_GATES below.
 */
export type GateKey =
  | 'hasChildren'
  | 'hasDependents'
  | 'hasPets'
  | 'ownsProperty'
  | 'ownsBusiness'
  | 'isRenter'
  | 'hasInvestments';

/**
 * Gate → the section slugs it controls.
 *
 * Explicit lists, never a prefix match. `children-dependents-gov-ids` IS gated
 * by `hasChildren` (s75 — it became a wizard card in the Children step #4, so a
 * childless client should not see it). The two `legal-business-*` sections carry
 * `wizardStage: null` (never wizard cards), so listing them under
 * `ownsBusiness` is a no-op for the wizard but lets the category page NA them.
 */
export const GATE_SECTIONS: Record<GateKey, readonly string[]> = {
  hasChildren: [
    SECTION_SLUGS.CHILDREN_DEPENDENTS_OVERVIEW,
    SECTION_SLUGS.CHILDREN_DEPENDENTS_HEALTH,
    SECTION_SLUGS.CHILDREN_DEPENDENTS_DAILY_CARE,
    SECTION_SLUGS.CHILDREN_DEPENDENTS_EDUCATION,
    SECTION_SLUGS.CHILDREN_DEPENDENTS_GUARDIANSHIP,
    SECTION_SLUGS.CHILDREN_DEPENDENTS_OTHER,
    SECTION_SLUGS.CHILDREN_DEPENDENTS_VALUES_WISHES,
    SECTION_SLUGS.CHILDREN_DEPENDENTS_FINANCIAL_SUPPORT,
    SECTION_SLUGS.CHILDREN_DEPENDENTS_GOV_IDS,
  ],
  // Controls no sections of its own — it only widens the Children & Dependents
  // category-level N/A to "children OR dependents" via CATEGORY_OR_GATES (s79).
  hasDependents: [],
  hasPets: [SECTION_SLUGS.FAMILY_PETS],
  ownsProperty: [SECTION_SLUGS.ASSETS_PROPERTIES],
  ownsBusiness: [
    SECTION_SLUGS.FINANCIAL_BUSINESS_SELF_EMPLOYMENT,
    SECTION_SLUGS.LEGAL_BUSINESS_DOCUMENTS,
    SECTION_SLUGS.LEGAL_BUSINESS_CONTACTS,
  ],
  // Wizard-only gates — OUT of scope for auto-NA (locked gate set is the 4 above).
  isRenter: [SECTION_SLUGS.FINANCIAL_AND_INSURANCE_HOUSING],
  hasInvestments: [
    SECTION_SLUGS.FINANCIAL_INVESTMENTS_RRSP,
    SECTION_SLUGS.FINANCIAL_INCOME_PENSIONS,
  ],
};

/**
 * The locked auto-NA gates (decision 36.8 / session 73; `hasDependents` added
 * s79). `hasDependents` is an auto-NA gate so the About You toggle can write it,
 * but it owns no sections (GATE_SECTIONS = []) — it only co-gates the Children &
 * Dependents category via CATEGORY_OR_GATES.
 */
// 🔴 s191 (D-191.13): `hasDependents` is READ HERE — it is a live auto-N/A gate. It is NOT read by
// any essential conditional (`guardianship_plan` gates on `hasChildren` alone), and a s191 census
// briefly mis-filed it as dead plumbing on that basis. The auto-N/A gating IS its consumer.
export const AUTO_NA_GATES = ['hasChildren', 'hasDependents', 'hasPets', 'ownsProperty', 'ownsBusiness'] as const;
export type AutoNaGate = typeof AUTO_NA_GATES[number];

/**
 * Categories that collapse to ONE category-level NA treatment when their gate
 * is false, rather than per-section. Children & Dependents only (session 73);
 * the manual_only `children-dependents-gov-ids` keeps rendering on its own rule.
 *
 * The gate named here is the PRIMARY gate — it sources the section list that
 * collapses (GATE_SECTIONS[gate]). The N/A *condition* for a category that also
 * appears in CATEGORY_OR_GATES is the AND-of-all-OR-gates, not this single gate.
 */
export const CATEGORY_LEVEL_GATE: Record<string, AutoNaGate> = {
  [CATEGORY_SLUGS.CHILDREN_AND_DEPENDENTS]: 'hasChildren',
};

/**
 * Category → the set of gates OR'd for its category-level N/A (s79 D1). The
 * category is N/A only when EVERY listed gate is explicitly `false` (any `true`
 * keeps it visible; any `null` renders normally). Children & Dependents is N/A
 * only when the planner has neither children nor dependents. The collapsing
 * section list still comes from CATEGORY_LEVEL_GATE's primary gate.
 */
// s108 (DB-S5): the two-flag OR-gate is retired. `hasChildren` and `hasDependents`
// collapsed into the single `hasChildren` flag ("Do you have any children or
// dependents?"), so Children & Dependents now N/As on that one gate via
// CATEGORY_LEVEL_GATE — no OR-gate entry needed. Consumers fall back to the single
// CATEGORY_LEVEL_GATE gate when a category is absent here. Map kept (still imported)
// but empty; re-add an entry only for a genuine multi-flag OR-gate.
export const CATEGORY_OR_GATES: Record<string, readonly AutoNaGate[]> = {};

/**
 * Section slug → gate, for the auto-NA gates handled at SECTION level
 * (pets, property, business). Excludes any gate handled at category level.
 */
export const SECTION_NA_GATE: Record<string, AutoNaGate> = (() => {
  const out: Record<string, AutoNaGate> = {};
  const categoryLevel = new Set<AutoNaGate>(Object.values(CATEGORY_LEVEL_GATE));
  for (const gate of AUTO_NA_GATES) {
    if (categoryLevel.has(gate)) continue;
    for (const slug of GATE_SECTIONS[gate]) out[slug] = gate;
  }
  return out;
})();

/** Section slug → gate, across ALL wizard gates (drives the wizard filter). */
export const WIZARD_SECTION_GATE: Record<string, GateKey> = (() => {
  const out: Record<string, GateKey> = {};
  for (const gate of Object.keys(GATE_SECTIONS) as GateKey[]) {
    for (const slug of GATE_SECTIONS[gate]) out[slug] = gate;
  }
  return out;
})();

/** Friendly, gate-level name for confirmation copy ("…tuck the {name} section away"). */
export const GATE_DISPLAY_NAME: Record<AutoNaGate, string> = {
  hasChildren: 'Children & Dependants',
  hasDependents: 'Children & Dependants',
  hasPets: 'Pets',
  ownsProperty: 'Properties',
  ownsBusiness: 'Business',
};

/** The client situational flags as they sit on the `clients` row (camelCase gate keys). */
export type SituationFlags = Partial<Record<AutoNaGate, boolean | null>>;

/**
 * Auto-N/A for a section, mirroring `GenericCategoryPage` EXACTLY (s73 + s79
 * OR-gate): category-level N/A (Children & Dependents — N/A only when EVERY
 * OR-gate is explicitly false) collapses its gated sections; section-level gates
 * (pets / property / business) N/A their one section when that flag is false.
 * A `null`/`true` flag never marks N/A.
 *
 * 🔴 s159 (LENS-POOLS-1, R3): MOVED here from `client/src/lib/next-section.ts`, unchanged, because
 * the planner lens (server-side) must ask the same question and a server module cannot import from
 * `client/`. `next-section.ts` re-exports it, so every existing client import path still resolves to
 * THIS function — one predicate, three callers (next-up walk, category page, lens). Do not fork it.
 */
export function isSectionAutoNA(
  categorySlug: string,
  sectionSlug: string,
  flags: SituationFlags,
  slugsWithRows?: ReadonlySet<string>,
): boolean {
  // s162 (CATEGORY-GATE-OR-ROWS-1) — flag-OR-rows. A `false` flag suppresses ONLY when the
  // gated surface is actually empty; a section holding real user records is never auto-N/A.
  // This is the same rule the EXECUTOR task gate has applied since s130 (D-130.1,
  // `server/task-gate.ts` GATE_TOKEN_SIGNAL_SECTIONS) — section auto-N/A simply never got it,
  // which is how a client with four children saw "Children and Dependents is tucked away".
  //
  // `slugsWithRows` is PRECOMPUTED by the caller (server: `computeSectionEmptiness`; client: the
  // section-overview aggregate's `hasRealRows`). It is deliberately never computed in here —
  // three callers computing row presence independently is exactly how one product came to hold
  // two different answers to "does this client have children".
  //
  // Omitting the argument preserves the pre-s162 flag-only behaviour, so callers migrate one at
  // a time rather than in a single flag day. `null`/`true` paths are untouched: null is
  // "unanswered", never a no.
  const hasRows = (slug: string) => slugsWithRows?.has(slug) ?? false;

  const catGate = CATEGORY_LEVEL_GATE[categorySlug];
  if (catGate) {
    const orGates = CATEGORY_OR_GATES[categorySlug];
    const naCondition = orGates
      ? orGates.every((g) => flags[g] === false)
      : flags[catGate] === false;
    const gatedSlugs = GATE_SECTIONS[catGate];
    // Rows ANYWHERE in the category's gated set keep the whole category rendering — the
    // category-level affordance is all-or-nothing, so one populated section un-tucks it.
    const categoryHasRows = gatedSlugs.some(hasRows);
    if (naCondition && !categoryHasRows && new Set<string>(gatedSlugs).has(sectionSlug)) {
      return true;
    }
  }
  const secGate = SECTION_NA_GATE[sectionSlug];
  if (secGate && flags[secGate] === false && !hasRows(sectionSlug)) return true;
  return false;
}

/**
 * Manual N/A escape — eligible sections (s85, D-84.8).
 *
 * Optional NON-gating sections a planner can mark "Not Applicable" by hand (a
 * parallel path to the auto-NA gates above — these are NOT promoted to auto-NA,
 * which would grow the About You toggle list + the auto-cascade). Marking writes
 * a `client_section_na` row; the section then renders the tucked-away
 * SectionNotApplicable state and drops out of NEXT UP until "Bring it back".
 *
 * Derived from the session-84 Phase 0 qualifying list: active + rendered (not
 * HIDDEN_SECTIONS) + NOT essential (ESSENTIAL_SECTION_SLUGS — those gate the
 * Estate Ready Score) + NOT auto-NA gated by a LOCKED gate. The 3 "ambiguous"
 * sections (Investments / Income & Pensions / Things I Rent) ARE included: their
 * gates (`hasInvestments`/`isRenter`) are wizard-only, so they render normally on
 * the category page and get the manual escape. `beneficiaries` from the Phase 0
 * list is intentionally EXCLUDED — it is an essential (ESSENTIAL_SECTION_SLUGS),
 * i.e. gating, and this feature is for non-gating sections only.
 */
export const MANUAL_NA_ELIGIBLE_SECTIONS: ReadonlySet<string> = new Set<string>([
  SECTION_SLUGS.LEGAL_POWERS_OF_ATTORNEY,
  SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_LETTERS,
  SECTION_SLUGS.FINANCIAL_DEBTS_LIABILITIES,
  SECTION_SLUGS.FINANCIAL_TAXES,
  SECTION_SLUGS.FINANCIAL_AND_INSURANCE_PRE_AUTHORIZED_DEBITS,
  SECTION_SLUGS.ASSETS_VEHICLE_LIST,
  SECTION_SLUGS.ASSETS_VALUABLES,
  // PERSONAL_INFORMATION_HEALTH_MEDICAL retired (FIELD-AUDIT-1 D-154.5) — dropped from manual-NA.
  SECTION_SLUGS.PERSONAL_INFORMATION_HEALTH_INSURANCE,
  SECTION_SLUGS.FAMILY_FRIENDS_AND_PETS_IMPORTANT_CONTACTS,
  // ── 🔴 s178 (A3, D-178.5): the three password-log sections LEFT this set ──────────────────
  // `password-log-government-tax`, `password-log-online-accounts-communities` and
  // `password-log-digital-files-cloud-storage` were eligible here from s85. D-177.9b then made
  // all five password-log sections SCORING-EXEMPT — no completion criterion at all — which left
  // three of them offering an affordance whose only effect was to put an unscored section back
  // into the denominator as a scored, complete one. "This doesn't apply to me" answers a
  // question the product has stopped asking of these sections, so the affordance goes.
  //
  // ⏳ Same time-box as the exemption itself (PLANNER-MAGIC-1). When the interview replaces the
  // recognition checklists with real questions, scoring resumes — and whether the N/A exit comes
  // back with it is a decision for that build, not an omission to quietly reverse here.
  // (PASSWORD_LOG_SHOPPING_LOYALTY_PROGRAMS had already folded into Online Accounts, D-152.11.)
  // The 3 ambiguous (wizard-only gated → render normally on the category page):
  SECTION_SLUGS.FINANCIAL_INVESTMENTS_RRSP,
  SECTION_SLUGS.FINANCIAL_INCOME_PENSIONS,
  SECTION_SLUGS.FINANCIAL_AND_INSURANCE_HOUSING,

  // ── 🔴 s177 (A2, D-177.7): the shaded sections join ──────────────────────────
  // After the suggestion-row retirement (D-177.4) a shaded section can legitimately be
  // EMPTY, and an empty section is not done (D-177.5b). "This doesn't apply to me" is the
  // one honest way to finish it, so the affordance must reach every shaded section.
  //
  // D-177.7 extends this to ESSENTIAL sections too — the 8 below include 6 essentials —
  // which is consistent with the s86 precedent already above (debts / investments / income
  // are essentials and have been eligible for a year). The guardrail is a ONE-TIME
  // plain-language confirm on essential sections, not a narrower list.
  SECTION_SLUGS.FINANCIAL_BANK_ACCOUNTS,
  SECTION_SLUGS.FINANCIAL_AND_INSURANCE_INSURANCE_POLICIES,
  SECTION_SLUGS.LEGAL_PROFESSIONAL_CONTACTS,
  SECTION_SLUGS.LEGAL_IMPORTANT_DOCUMENTS,
  SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_FUNERAL_WISHES,
  SECTION_SLUGS.ASSETS_PROPERTIES,
  SECTION_SLUGS.PERSONAL_INFORMATION_CAREER,
  SECTION_SLUGS.FAMILY_PETS,
]);

/**
 * 🔴 PERMANENTLY INELIGIBLE for "This doesn't apply to me" — D-177.8 + D-177.14.
 *
 * These two are NOT omissions from the list above; they are ruled exclusions, and the
 * distinction is worth naming because both would otherwise arrive automatically from the
 * mechanical "every shaded section" rule D-177.7 states.
 *
 * The shared reasoning: for these two, a blank section means UNDECIDED, never INAPPLICABLE.
 *   • `legal-your-executor` (D-177.8) — every estate has an executor. An intestate estate
 *     gets a court-appointed trustee; not having chosen one yet is a decision outstanding,
 *     not a fact absent. The section completes ONE way: by naming someone.
 *   • `personal-information-government-ids` (D-177.14) — every adult has a SIN. Its four
 *     rows are LOCKED built-ins that never retire, and six gov-form bindings resolve
 *     against them, so "doesn't apply" would contradict the forms the executor must file.
 *
 * Exported so the exclusion is a value the UI and the tests both read, rather than an
 * absence anyone can silently reverse by appending one line to the set above.
 */
export const NEVER_NA_ELIGIBLE_SECTIONS: ReadonlySet<string> = new Set<string>([
  SECTION_SLUGS.LEGAL_YOUR_EXECUTOR,
  SECTION_SLUGS.PERSONAL_INFORMATION_GOVERNMENT_IDS,
]);

/** The ONE predicate for "may this section be marked doesn't-apply?" (s177, A2).
 *  Callers must use this rather than testing `MANUAL_NA_ELIGIBLE_SECTIONS` directly, so a
 *  ruled exclusion cannot be defeated by adding the slug to the eligible set. */
export function isManualNaEligibleSection(sectionSlug: string): boolean {
  if (NEVER_NA_ELIGIBLE_SECTIONS.has(sectionSlug)) return false;
  return MANUAL_NA_ELIGIBLE_SECTIONS.has(sectionSlug);
}

/**
 * About You yes/no field key → the gate (clients column) it now reads & writes.
 * Note the historical casing mismatch in the field keys (camel vs snake).
 */
export const ABOUT_YOU_GATE_FIELDS: Record<string, AutoNaGate> = {
  hasChildren: 'hasChildren',
  hasDependents: 'hasDependents',
  hasPets: 'hasPets',
  owns_business: 'ownsBusiness',
  owns_property: 'ownsProperty',
};

/**
 * s119 — Capture-only About You flags. Like the gate flags above, an About You yes/no
 * toggle listed here persists to its `clients` column via `PATCH …/situation-flags`
 * (value = the camelCase `clients` column key). UNLIKE the gate flags, a capture flag
 * gates NO section — it is deliberately absent from GateKey / GATE_SECTIONS / AUTO_NA_GATES,
 * so it never tucks a section away and never shows the tuck-away confirmation. The write
 * is silent (mirrors the work_situation → ownsBusiness propagation). Purpose: accumulate a
 * planner-side signal that gates a FUTURE executor task (has_firearms → manage-firearms,
 * canonical task list v2.1). A field must be in EITHER this map OR ABOUT_YOU_GATE_FIELDS,
 * never both.
 */
export const ABOUT_YOU_CAPTURE_FLAG_FIELDS: Record<string, string> = {
  hasFirearms: 'hasFirearms',
  // s171 (R-A): military/RCMP service. Capture-only for the same reason firearms is —
  // it gates an executor task (check-veterans-benefits) but no planner section, so it must
  // NOT go in ABOUT_YOU_GATE_FIELDS. Unlike firearms it is flag-only by necessity rather
  // than by choice: no section of the plan records service history, so there is nothing a
  // flag-OR-rows signal check could read.
  hasVeteransService: 'hasVeteransService',
};

/**
 * s119 — On-"Yes" guidance line shown beneath a capture-only toggle (standard muted
 * guidance-line copy, NOT a warning banner). Keyed by About You fieldKey. Rendered only
 * when the toggle reads "true".
 */
export const ABOUT_YOU_TOGGLE_HINTS: Record<string, string> = {
  hasFirearms:
    "Record each firearm's make, model, and serial number under Assets → Valuables (category: Firearms), plus your licence number and where it's kept — your executor will need these exact details for the RCMP transfer forms. Keep safe combinations in your Secure Information File.",
  hasVeteransService:
    "Keep your service number and discharge papers somewhere your executor can find them — Veterans Affairs Canada administers funeral and burial benefits and a survivor's pension, and both are claimed against your service record.",
};
